import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import {
  AdminGetAgencyQuery,
  AdminUpdateAgencyReportDictionaryMutation,
  AgencyOfficerRow,
  VocabularyInput,
} from '@clipr/lib';

import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import {
  AgencyDetailsForm,
  createAgencyDetailsForm,
  toAgencyDetailsInput,
} from '../../components/agencyDetails';

export type AdminAgencyDetail = AdminGetAgencyQuery['adminGetAgency'];
export type AdminAgencyOfficer = AdminAgencyDetail['officers'][number];
export type AdminAgencyLibrary = AdminAgencyDetail['libraries'][number];
export type AdminSavedReportDictionary =
  AdminUpdateAgencyReportDictionaryMutation['adminUpdateAgencyReportDictionary'];

export type AdminAgencyPageParams = {
  orgId: string;
};

export class AdminAgencyPageState extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this);
  }

  @observable agency: AdminAgencyDetail | null = null;
  @observable orgId: string | null = null;
  @observable isLoading = false;
  @observable error: string | null = null;

  @observable isAddOfficerOpen = false;
  @observable isAdding = false;
  @observable addOfficerError: string | null = null;
  @observable.shallow addOfficerForm: AgencyOfficerRow = emptyForm();

  @observable isEditOfficerOpen = false;
  @observable isUpdatingOfficer = false;
  @observable editOfficerError: string | null = null;
  @observable editOfficerCurrentEmail: string | null = null;
  @observable isEditOfficerEmailLocked = false;
  @observable.shallow editOfficerForm: AgencyOfficerRow = emptyForm();

  @observable isDictionaryOpen = false;
  @observable isSavingVocabulary = false;
  @observable vocabularyError: string | null = null;
  @observable.shallow vocabulary: VocabularyInput[] = [];

  @observable isDetailsOpen = false;
  @observable isSavingDetails = false;
  @observable detailsError: string | null = null;
  @observable.shallow detailsForm: AgencyDetailsForm = createAgencyDetailsForm(null);
  @observable isReportDictionaryOpen = false;
  @observable isSavingReportDictionary = false;
  @observable reportDictionaryError: string | null = null;
  @observable.shallow reportDictionary: { target: string; sourcesText: string }[] = [];
  private reportDictionarySaveSeq = 0;
  private confirmedClearReportDictionary = false;
  /**
   * The generation the open draft was composed against. Captured when the editor opens and never
   * refreshed while it stays open: taking it from a later refetch would hand a stale draft a fresh
   * generation, which is exactly the overwrite the guard exists to stop.
   */
  @observable editingReportDictionaryVersion = 0;
  /**
   * The report format the open editor writes to. Null means the agency-wide fallback, which every
   * format inherits -- what this feature was before, and still the right home for wording the whole
   * agency shares.
   */
  @observable editingSpecialtyId: string | null = null;
  /** Rule count the draft started from, so the clear-everything confirmation judges the right list. */
  private baselineRuleCount = 0;

  @observable isDeleteOpen = false;
  @observable isDeleting = false;
  @observable deleteError: string | null = null;
  @observable deleteConfirmInput = '';
  @observable isDeleted = false;

  @observable isSyncing = false;
  @observable syncError: string | null = null;
  @observable syncMessage: string | null = null;

  // Bumped per fetch so a slow response for a previous org can be dropped — this page state is a
  // reused singleton, so without it agency A could stay on screen (and get synced) under B's route.
  private fetchSeq = 0;
  private detailsSaveSeq = 0;
  // Same reasoning for the sync round-trip: A's result must never be reported under B.
  private syncSeq = 0;

  @computed
  get domain(): string {
    return this.agency?.agencyDomain ?? '';
  }

  @computed
  get hasSourceFormat(): boolean {
    return !!this.agency?.sourceCustomSpecialtyId;
  }

  /**
   * Rules are stored on the agency, which always exists, so a trained format is no longer a
   * precondition -- an agency that trained through the Train wizard has terminology and no source
   * format to hang it on. Still gated on having read the agency: the save replaces the whole array,
   * so composing against a dictionary we never loaded would delete whatever is really there.
   */
  @computed
  get canEditReportDictionary(): boolean {
    return !!this.agency;
  }

  @action
  async pageMounted(params: AdminAgencyPageParams) {
    this.orgId = params.orgId;
    // Drop anything from the previously viewed agency so a stale agency never shows — or gets
    // synced — under the new route while its fetch is in flight.
    this.agency = null;
    this.syncMessage = null;
    this.syncError = null;
    this.isSyncing = false;
    await this.fetch();
  }

  @action
  pageUnmounted() {
    this.error = null;
    this.isAddOfficerOpen = false;
    this.isDetailsOpen = false;
    this.isEditOfficerOpen = false;
    // The report editor is state on a page-level singleton, so leaving it open across a navigation
    // reopens it on the NEXT agency still holding the previous agency's rows -- and Save would write
    // them there. Close it and retire any in-flight save, same as the details editor below.
    this.isReportDictionaryOpen = false;
    this.isSavingReportDictionary = false;
    this.reportDictionaryError = null;
    this.reportDictionary = [];
    this.editingReportDictionaryVersion = 0;
    this.editingSpecialtyId = null;
    this.baselineRuleCount = 0;
    this.confirmedClearReportDictionary = false;
    this.reportDictionarySaveSeq++;
    // Returning to the SAME agency leaves orgId unchanged, so the org check alone would let a save
    // started before the unmount land on the fresh session. Retire the token instead.
    this.detailsSaveSeq++;
    // The retired save returns early and never clears these, so without resetting them here the
    // next editor opens stuck on "Saving…" with Close, Cancel and Save all disabled.
    this.isSavingDetails = false;
    this.detailsError = null;
  }

  @action
  async fetch() {
    const orgId = this.orgId;
    if (!orgId)
      return;

    const seq = ++this.fetchSeq;
    this.isLoading = true;
    this.error = null;

    const [res, err] = await this.store.apiService.adminGetAgency({ orgId });

    runInAction(() => {
      // A newer fetch or a route change superseded this response — discard it.
      if (seq !== this.fetchSeq || orgId !== this.orgId)
        return;
      this.isLoading = false;
      if (err) {
        this.error = err.message;
        return;
      }
      this.agency = res?.adminGetAgency ?? null;
    });
  }

  @action
  openAddOfficer() {
    this.addOfficerForm = emptyForm();
    this.addOfficerError = null;
    this.isAddOfficerOpen = true;
  }

  @action
  closeAddOfficer() {
    this.isAddOfficerOpen = false;
    this.addOfficerError = null;
  }

  @action
  openDetails() {
    this.detailsForm = createAgencyDetailsForm(this.agency);
    this.detailsError = null;
    this.isDetailsOpen = true;
  }

  @action
  closeDetails() {
    if (this.isSavingDetails) return;
    this.isDetailsOpen = false;
    this.detailsError = null;
  }

  @action
  updateDetails(patch: Partial<AgencyDetailsForm>) {
    this.detailsForm = { ...this.detailsForm, ...patch };
  }

  @action
  async submitDetails() {
    const orgId = this.orgId;
    if (!orgId) return;

    const [details, validationError] = toAgencyDetailsInput(this.detailsForm);
    if (!details) {
      this.detailsError = validationError;
      return;
    }

    // Same guard fetch() uses: this state outlives the route, so a slow save for agency A must not
    // close the modal, report its error, or trigger a refetch once the page has moved to agency B.
    const seq = ++this.detailsSaveSeq;
    this.isSavingDetails = true;
    this.detailsError = null;

    const [, err] = await this.store.apiService.adminUpdateAgencyDetails({ orgId, details });

    const superseded = seq !== this.detailsSaveSeq || orgId !== this.orgId;
    runInAction(() => {
      if (superseded)
        return;
      this.isSavingDetails = false;
      if (err) {
        this.detailsError = err.message;
        return;
      }
      this.isDetailsOpen = false;
    });

    if (!err && !superseded)
      await this.fetch();
  }

  @action
  updateAddOfficer(patch: Partial<AgencyOfficerRow>) {
    this.addOfficerForm = { ...this.addOfficerForm, ...patch };
  }

  @action
  async submitAddOfficer() {
    if (!this.orgId)
      return;

    const validation = validate(this.addOfficerForm, this.domain);
    if (validation) {
      this.addOfficerError = validation;
      return;
    }

    this.isAdding = true;
    this.addOfficerError = null;

    const officer: AgencyOfficerRow = {
      firstName: this.addOfficerForm.firstName.trim(),
      lastName: this.addOfficerForm.lastName.trim(),
      title: this.addOfficerForm.title.trim(),
      email: this.addOfficerForm.email.trim().toLowerCase(),
      supervisorTitle: this.addOfficerForm.supervisorTitle.trim(),
      supervisorName: this.addOfficerForm.supervisorName.trim(),
      supervisorEmail: this.addOfficerForm.supervisorEmail.trim().toLowerCase(),
    };

    const [, err] = await this.store.apiService.adminAddOfficerToAgency({ orgId: this.orgId, officer });

    runInAction(() => {
      this.isAdding = false;
      if (err) {
        this.addOfficerError = err.message;
        return;
      }
      this.isAddOfficerOpen = false;
    });

    if (!err)
      await this.fetch();
  }

  @action
  openEditOfficer(officer: AdminAgencyOfficer) {
    this.editOfficerCurrentEmail = officer.email;
    this.isEditOfficerEmailLocked = !!officer.teamId;
    this.editOfficerForm = {
      firstName: officer.firstName ?? '',
      lastName: officer.lastName ?? '',
      title: officer.title ?? '',
      email: officer.email ?? '',
      supervisorTitle: officer.supervisorTitle ?? '',
      supervisorName: officer.supervisorName ?? '',
      supervisorEmail: officer.supervisorEmail ?? '',
    };
    this.editOfficerError = null;
    this.isEditOfficerOpen = true;
  }

  @action
  closeEditOfficer() {
    if (this.isUpdatingOfficer) return;
    this.isEditOfficerOpen = false;
    this.editOfficerError = null;
    this.editOfficerCurrentEmail = null;
    this.isEditOfficerEmailLocked = false;
  }

  @action
  updateEditOfficer(patch: Partial<AgencyOfficerRow>) {
    this.editOfficerForm = { ...this.editOfficerForm, ...patch };
  }

  @action
  async submitEditOfficer() {
    if (!this.orgId || !this.editOfficerCurrentEmail)
      return;

    const validation = validate(this.editOfficerForm, this.domain);
    if (validation) {
      this.editOfficerError = validation;
      return;
    }

    this.isUpdatingOfficer = true;
    this.editOfficerError = null;

    const officer: AgencyOfficerRow = normalizeOfficerForm(this.editOfficerForm);

    const [, err] = await this.store.apiService.adminUpdateAgencyOfficer({
      orgId: this.orgId,
      currentEmail: this.editOfficerCurrentEmail,
      officer,
    });

    runInAction(() => {
      this.isUpdatingOfficer = false;
      if (err) {
        this.editOfficerError = err.message;
        return;
      }
      this.isEditOfficerOpen = false;
      this.editOfficerCurrentEmail = null;
      this.isEditOfficerEmailLocked = false;
    });

    if (!err)
      await this.fetch();
  }

  @action
  openDictionary() {
    this.vocabulary = (this.agency?.vocabulary ?? []).map(v => ({
      phrase: v.phrase,
      displayAs: v.displayAs ?? undefined,
    }));
    this.vocabularyError = null;
    this.isDictionaryOpen = true;
  }

  @action
  closeDictionary() {
    if (this.isSavingVocabulary) return;
    this.isDictionaryOpen = false;
    this.vocabularyError = null;
  }

  @action
  addVocabularyRow() {
    this.vocabulary = [...this.vocabulary, { phrase: '', displayAs: '' }];
  }

  @action
  updateVocabularyRow(index: number, patch: Partial<VocabularyInput>) {
    this.vocabulary = this.vocabulary.map((row, i) => (i === index ? { ...row, ...patch } : row));
  }

  @action
  removeVocabularyRow(index: number) {
    this.vocabulary = this.vocabulary.filter((_, i) => i !== index);
  }

  @action
  async submitVocabulary() {
    if (!this.orgId) return;

    const vocabulary = this.vocabulary
      .map(row => ({
        phrase: row.phrase.trim(),
        displayAs: row.displayAs?.trim() || undefined,
      }))
      .filter(row => row.phrase);

    const phrases = vocabulary.map(v => v.phrase.toLowerCase());
    if (phrases.length !== new Set(phrases).size) {
      this.vocabularyError = 'Duplicate phrases are not allowed';
      return;
    }

    this.isSavingVocabulary = true;
    this.vocabularyError = null;

    const [, err] = await this.store.apiService.adminUpdateAgencyVocabulary({ orgId: this.orgId, vocabulary });

    runInAction(() => {
      this.isSavingVocabulary = false;
      if (err) {
        this.vocabularyError = err.message;
        return;
      }
      this.isDictionaryOpen = false;
    });

    if (!err)
      await this.fetch();
  }

  @action
  openReportDictionary(specialtyId: string | null = null) {
    if (!this.canEditReportDictionary || !this.agency) return;
    this.loadReportDictionaryFor(specialtyId);
    this.isReportDictionaryOpen = true;
  }

  /** Switch the open editor to another format. Discards the draft — it belonged to the other one. */
  @action
  selectReportDictionaryFormat(specialtyId: string | null) {
    if (this.isSavingReportDictionary) return;
    this.loadReportDictionaryFor(specialtyId);
  }

  @action
  private loadReportDictionaryFor(specialtyId: string | null) {
    const format = specialtyId
      ? this.agency?.reportFormats.find(f => f.specialtyId === specialtyId)
      : undefined;
    // An id that names no format of this agency falls back to the agency-wide list rather than
    // opening an editor over rules it would then save somewhere else.
    const rules = format?.reportDictionary ?? this.agency?.reportDictionary ?? [];

    this.reportDictionary = rules.map(rule => ({
      target: rule.target,
      sourcesText: rule.sources.join(', '),
    }));
    this.editingSpecialtyId = format?.specialtyId ?? null;
    this.editingReportDictionaryVersion = format
      ? format.reportDictionaryVersion
      : this.agency?.reportDictionaryVersion ?? 0;
    this.baselineRuleCount = rules.length;
    this.reportDictionaryError = null;
    this.confirmedClearReportDictionary = false;
  }

  @action
  closeReportDictionary() {
    if (this.isSavingReportDictionary) return;
    this.isReportDictionaryOpen = false;
    this.reportDictionaryError = null;
  }

  @action
  addReportDictionaryRow() {
    this.reportDictionary = [...this.reportDictionary, { target: '', sourcesText: '' }];
  }

  @action
  updateReportDictionaryRow(index: number, patch: Partial<{ target: string; sourcesText: string }>) {
    this.reportDictionary = this.reportDictionary.map((row, i) => (i === index ? { ...row, ...patch } : row));
  }

  @action
  removeReportDictionaryRow(index: number) {
    this.reportDictionary = this.reportDictionary.filter((_, i) => i !== index);
  }

  @action
  async submitReportDictionary() {
    const orgId = this.orgId;
    const agency = this.agency;
    if (!orgId || !agency) return;

    // Rows are rejected, never silently dropped. A whole-array save means a row the user can still
    // see on screen but that normalizes to nothing would delete that rule without ever saying so.
    const rows = this.reportDictionary.map(row => ({
      target: row.target.trim(),
      // Either separator. A comma is what people type and what the table shows; newlines make a
      // list pasted from a document work without reformatting. Neither can occur inside a term --
      // validation rejects control characters, and a term here is a single common noun, not a name
      // (names belong in the transcript dictionary, where commas are left alone).
      sources: row.sourcesText.split(/[\n,]/).map(s => s.trim()).filter(Boolean),
    }));

    if (rows.some(row => !row.target || row.sources.length === 0)) {
      this.reportDictionaryError = 'Every rule needs a replacement term and at least one term to replace';
      return;
    }

    // NFKC first, matching the server. Without it a full-width confusable passes here and is
    // rejected on the server, which reads as an unexplained failure at the end of a long edit.
    const targets = rows.map(r => r.target.normalize('NFKC').toLowerCase());
    if (targets.length !== new Set(targets).size) {
      this.reportDictionaryError = 'Duplicate targets are not allowed';
      return;
    }


    // A whole-array save, so submitting an empty editor deletes every rule the agency had. The one
    // edit here that is destructive rather than corrective, and the one worth confirming.
    if (rows.length === 0 && this.baselineRuleCount > 0 && !this.confirmedClearReportDictionary) {
      this.reportDictionaryError = 'This removes every report rule. Save again to confirm.';
      this.confirmedClearReportDictionary = true;
      return;
    }

    this.isSavingReportDictionary = true;
    this.reportDictionaryError = null;

    // Captured, not re-read on completion: this page is a singleton, so by the time the mutation
    // returns `this.orgId` may already be a different agency.
    const seq = this.reportDictionarySaveSeq;
    const editedSpecialtyId = this.editingSpecialtyId;
    const [res, err] = await this.store.apiService.adminUpdateAgencyReportDictionary({
      orgId,
      reportDictionary: rows,
      expectedVersion: this.editingReportDictionaryVersion,
      specialtyId: editedSpecialtyId ?? undefined,
    });

    if (seq !== this.reportDictionarySaveSeq || orgId !== this.orgId) return;

    runInAction(() => {
      this.isSavingReportDictionary = false;
      if (err) {
        this.reportDictionaryError = err.message;
        return;
      }
      // Applied from what the server says it committed, rather than waiting on the refetch below.
      // The refetch can fail, and a page still showing the pre-save generation sends it back as
      // `expectedVersion` on the next save -- a conflict with this save, forever.
      this.applySavedReportDictionary(res?.adminUpdateAgencyReportDictionary, editedSpecialtyId);
      this.isReportDictionaryOpen = false;
    });

    // Still refetched: the rest of the page (rule counts, other formats, a library provisioned
    // meanwhile) is not in the mutation's answer. Failure here no longer costs the editor anything.
    await this.fetch();
  }

  /**
   * Folds a committed save into the agency on screen.
   *
   * Keyed on the list the EDITOR was on, not the one the server names: an agency-wide save at an
   * agency with a registered format lands on that format and is mirrored to the agency, so the
   * response carries a format id while the list being edited was the agency-wide one.
   */
  @action
  private applySavedReportDictionary(
    saved: AdminSavedReportDictionary | undefined,
    editedSpecialtyId: string | null,
  ) {
    if (!saved || !this.agency) return;

    this.agency = {
      ...this.agency,
      ...(editedSpecialtyId ? {} : {
        reportDictionary: saved.reportDictionary,
        reportDictionaryVersion: saved.reportDictionaryVersion,
      }),
      reportFormats: this.agency.reportFormats.map(format => (
        format.specialtyId === saved.specialtyId
          ? {
            ...format,
            reportDictionary: saved.reportDictionary,
            reportDictionaryVersion: saved.reportDictionaryVersion,
          }
          : format
      )),
    };
  }

  @action
  async syncLibraries() {
    // Guard against the singleton showing a stale agency: only sync when the displayed agency is
    // the one the current route points at, so we can never sync a different org than what's on screen.
    const orgId = this.orgId;
    if (!orgId || this.agency?.orgId !== orgId) return;

    const seq = ++this.syncSeq;
    this.isSyncing = true;
    this.syncError = null;
    this.syncMessage = null;

    const [res, err] = await this.store.apiService.adminSyncAgencyLibraries({ orgId });

    // A route change (or a newer sync) superseded this run — its outcome belongs to an agency that
    // is no longer on screen, so reporting it here would attribute A's result to B.
    const superseded = seq !== this.syncSeq || orgId !== this.orgId;

    runInAction(() => {
      if (superseded)
        return;
      this.isSyncing = false;
      if (err) {
        this.syncError = err.message;
        return;
      }
      const count = res?.adminSyncAgencyLibraries.length ?? 0;
      this.syncMessage = count === 0
        ? 'No libraries needed syncing.'
        : `Synced the report format into ${count} ${count === 1 ? 'library' : 'libraries'}.`;
    });

    if (!err && !superseded)
      await this.fetch();
  }

  @action
  openDelete() {
    this.isDeleteOpen = true;
    this.deleteError = null;
    this.deleteConfirmInput = '';
  }

  @action
  closeDelete() {
    if (this.isDeleting) return;
    this.isDeleteOpen = false;
    this.deleteError = null;
  }

  @action
  setDeleteConfirm(value: string) {
    this.deleteConfirmInput = value;
  }

  @computed
  get canSubmitDelete(): boolean {
    return !!this.agency
      && this.deleteConfirmInput.trim() === this.agency.agencyName
      && !this.isDeleting;
  }

  @action
  async submitDelete(): Promise<boolean> {
    if (!this.orgId || !this.agency) return false;
    if (this.deleteConfirmInput.trim() !== this.agency.agencyName) {
      this.deleteError = `Type "${this.agency.agencyName}" to confirm`;
      return false;
    }

    this.isDeleting = true;
    this.deleteError = null;

    const [, err] = await this.store.apiService.adminHardDeleteAgency({ orgId: this.orgId });

    runInAction(() => {
      this.isDeleting = false;
      if (err) {
        this.deleteError = err.message;
        return;
      }
      this.isDeleteOpen = false;
      this.isDeleted = true;
    });

    return !err;
  }
}

function emptyForm(): AgencyOfficerRow {
  return {
    firstName: '',
    lastName: '',
    title: '',
    email: '',
    supervisorTitle: '',
    supervisorName: '',
    supervisorEmail: '',
  };
}

function normalizeOfficerForm(form: AgencyOfficerRow): AgencyOfficerRow {
  return {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    title: form.title.trim(),
    email: form.email.trim().toLowerCase(),
    supervisorTitle: form.supervisorTitle.trim(),
    supervisorName: form.supervisorName.trim(),
    supervisorEmail: form.supervisorEmail.trim().toLowerCase(),
  };
}

const FIELD_LABELS: Record<keyof AgencyOfficerRow, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  title: 'Title',
  email: 'Email',
  supervisorTitle: 'Supervisor title',
  supervisorName: 'Supervisor name',
  supervisorEmail: 'Supervisor email',
};

function validate(row: AgencyOfficerRow, domain: string): string | null {
  const required = Object.keys(FIELD_LABELS) as (keyof AgencyOfficerRow)[];
  for (const key of required) {
    if (!row[key]?.trim())
      return `${FIELD_LABELS[key]} is required`;
  }

  const email = row.email.trim();
  const supervisorEmail = row.supervisorEmail.trim();

  if (!/^\S+@\S+\.\S+$/.test(email))
    return 'Officer email is invalid';

  if (!/^\S+@\S+\.\S+$/.test(supervisorEmail))
    return 'Supervisor email is invalid';

  if (domain) {
    const lc = domain.toLowerCase();
    if (email.split('@')[1]?.toLowerCase() !== lc)
      return `Officer email must use @${domain}`;
    if (supervisorEmail.split('@')[1]?.toLowerCase() !== lc)
      return `Supervisor email must use @${domain}`;
  }

  return null;
}
